import React, { useEffect, useRef, useState } from "react";
import { Loader2, Sparkles, Wand2, ExternalLink, ArrowRight, Lock, BadgeCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";

/**
 * Anonymous AI Website Builder widget — embedded on the landing page.
 * Lets visitors generate up to 5 site previews without an account.
 * Preview iframes the rendered HTML (watermarked) and prompts subscription on Publish.
 */
const TYPES = [
  { id: "business",   label: "Business site" },
  { id: "ecommerce",  label: "Ecommerce" },
  { id: "portfolio",  label: "Portfolio" },
  { id: "landing",    label: "Landing page" },
];

const backend = process.env.REACT_APP_BACKEND_URL;

export default function HomeAIBuilder() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [form, setForm] = useState({
    business_name: "", industry: "", color_preference: "blue",
    website_type: "business", services: "", target_audience: "", tone: "professional",
  });
  const [creating, setCreating] = useState(false);
  const [credits, setCredits] = useState(null);
  const [job, setJob] = useState(null);
  const [previewSlug, setPreviewSlug] = useState(null);   // visitor preview key
  const previewRef = useRef(null);

  useEffect(() => {
    api.get("/ai/public/credits", { withCredentials: true })
      .then(r => setCredits(r.data))
      .catch(() => {});
  }, []);

  const remaining = (credits?.credits_remaining ?? 5);

  const setF = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const generate = async () => {
    if (!form.business_name || !form.industry) { toast.error("Business name and industry are required"); return; }
    if (remaining <= 0) { toast.error("Out of free credits — sign up to subscribe & keep building."); nav("/signup?next=/pricing"); return; }
    setCreating(true);
    try {
      const payload = { ...form, services: form.services.split(",").map(s=>s.trim()).filter(Boolean) };
      const { data } = await api.post("/ai/public/website-builder", payload, { withCredentials: true });
      setJob(data);
      // Persist preview locally so user can come back after signup
      try {
        localStorage.setItem("optivize_anon_job", JSON.stringify({ id: data.id, visitor_id: data.visitor_id, at: Date.now() }));
      } catch {}
      setCredits({ credits_used: data.credits_total - data.credits_remaining, credits_total: data.credits_total, credits_remaining: data.credits_remaining });
      toast.success(`Preview ready · ${data.credits_remaining} free credits left`);
      setTimeout(() => previewRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    } catch (e) {
      const code = e?.response?.status;
      if (code === 429) {
        toast.error("Out of free credits — sign up to subscribe.");
        nav("/signup?next=/pricing");
      } else {
        toast.error(e?.response?.data?.detail || "Generation failed");
      }
    } finally { setCreating(false); }
  };

  const claimAndPublish = () => {
    if (!job) return;
    // Save the anon job so we can claim it after signup
    if (!user) {
      nav(`/signup?next=/ai-builder?job=${job.id}`);
    } else {
      nav(`/pricing?cat=ai_builder&job=${job.id}`);
    }
  };

  return (
    <section className="py-20 px-5" data-testid="home-ai-builder" style={{background:'#0a0f1f'}}>
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-blue-500/30 bg-blue-500/10 text-blue-300 text-xs font-semibold mb-4">
            <Sparkles size={12}/> Free preview · {remaining}/{credits?.credits_total ?? 5} credits left
          </div>
          <h2 className="text-4xl sm:text-5xl font-extrabold text-white" style={{fontFamily:'Outfit'}}>
            Design your website with <span style={{color:'var(--accent-yellow)'}}>AI</span> — in 60 seconds
          </h2>
          <p className="text-slate-400 mt-3 text-lg">Generate a full website preview before you sign up. Five free credits, no card required.</p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 items-start">
          <div className="card p-6">
            <h3 className="text-white font-semibold text-lg mb-4 flex items-center gap-2" style={{fontFamily:'Outfit'}}>
              <Wand2 size={18} className="text-cyan-300"/> Tell us about your business
            </h3>
            <div className="space-y-3">
              <div className="grid sm:grid-cols-2 gap-3">
                <div><label className="label">Business name *</label>
                  <input className="input" value={form.business_name} onChange={setF("business_name")} placeholder="Acme Studios" data-testid="hai-name"/></div>
                <div><label className="label">Industry *</label>
                  <input className="input" value={form.industry} onChange={setF("industry")} placeholder="Photography" data-testid="hai-industry"/></div>
              </div>
              <div><label className="label">Site type</label>
                <div className="flex flex-wrap gap-2">
                  {TYPES.map(t => (
                    <button key={t.id} type="button" onClick={() => setForm({...form, website_type: t.id})}
                      className={`px-3 py-1.5 rounded-lg text-xs transition border ${form.website_type===t.id ? "bg-blue-600 text-white border-blue-600" : "border-slate-700 text-slate-300 hover:border-blue-500"}`}
                      data-testid={`hai-type-${t.id}`}>{t.label}</button>
                  ))}
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div><label className="label">Color preference</label>
                  <select className="input" value={form.color_preference} onChange={setF("color_preference")} data-testid="hai-color">
                    {["blue","green","red","purple","orange","monochrome","brand-aligned"].map(c => <option key={c} value={c}>{c}</option>)}
                  </select></div>
                <div><label className="label">Tone</label>
                  <select className="input" value={form.tone} onChange={setF("tone")} data-testid="hai-tone">
                    {["professional","friendly","bold","minimal","luxury"].map(c => <option key={c} value={c}>{c}</option>)}
                  </select></div>
              </div>
              <div><label className="label">Services (comma-separated)</label>
                <input className="input" value={form.services} onChange={setF("services")} placeholder="Weddings, Pre-wedding, Events" data-testid="hai-services"/></div>
              <div><label className="label">Target audience</label>
                <input className="input" value={form.target_audience} onChange={setF("target_audience")} placeholder="Couples in their 20s-30s" data-testid="hai-audience"/></div>
            </div>
            <button onClick={generate} disabled={creating || remaining<=0}
              className="btn-primary mt-5 w-full inline-flex items-center justify-center gap-2" data-testid="hai-generate">
              {creating ? <Loader2 size={16} className="animate-spin"/> : <Sparkles size={16}/>}
              {remaining > 0 ? `Generate preview (1 credit)` : "Out of free credits"}
            </button>
            <p className="text-xs text-slate-500 mt-3">Previews include a "Built by Optivize" watermark. Subscribe to remove it and publish.</p>
          </div>

          <div className="card p-6 min-h-[460px] flex flex-col" ref={previewRef}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-white font-semibold text-lg flex items-center gap-2" style={{fontFamily:'Outfit'}}>
                <BadgeCheck size={18} className="text-emerald-300"/> Live preview
              </h3>
              {job && (
                <span className="text-xs text-slate-400">Job <code className="text-cyan-300">{job.id.slice(0,8)}</code></span>
              )}
            </div>
            {!job ? (
              <div className="flex-1 flex items-center justify-center text-center px-6">
                <div>
                  <Sparkles size={32} className="mx-auto text-slate-600 mb-3"/>
                  <p className="text-slate-500 text-sm">Fill in the brief on the left and click Generate. Your AI-designed website will appear here.</p>
                </div>
              </div>
            ) : (
              <>
                <div className="rounded-xl overflow-hidden border border-slate-700 bg-white" data-testid="hai-preview-card">
                  <PreviewIframe job={job}/>
                </div>
                <div className="mt-5 grid sm:grid-cols-2 gap-2">
                  <button onClick={()=>setJob(null)} className="btn-ghost text-sm" data-testid="hai-regenerate">Try another brief</button>
                  <button onClick={claimAndPublish} className="btn-primary text-sm inline-flex items-center justify-center gap-2" data-testid="hai-publish">
                    <Lock size={14}/> Publish — requires subscription <ArrowRight size={14}/>
                  </button>
                </div>
                <div className="mt-4 rounded-lg bg-amber-500/5 border border-amber-400/30 p-3 text-xs text-amber-200">
                  <strong>Preview-only.</strong> To publish at <code className="text-amber-100">{form.business_name.toLowerCase().replace(/\s+/g,'-').slice(0,12)||'yourbrand'}.optivize.digital</code> or your own domain, you need an active AI Website Builder plan (₹499–₹2,499/mo).
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}


/**
 * Renders the AI blueprint into an iframe via srcdoc — same renderer the
 * backend uses to publish, just with the watermark baked in.
 */
function PreviewIframe({ job }) {
  const html = buildPreviewHtml(job?.output || {});
  return (
    <iframe
      title="ai-preview"
      srcDoc={html}
      sandbox="allow-same-origin"
      style={{ width: "100%", height: "440px", border: 0, display: "block" }}
      data-testid="hai-preview-iframe"
    />
  );
}

function buildPreviewHtml(bp) {
  const headline = bp.hero_headline || "Welcome";
  const tagline = bp.tagline || "";
  const sub = bp.hero_subheadline || "";
  const cta = bp.primary_cta || "Get started";
  const palette = bp.color_palette || ["#0f172a","#1d4ed8","#facc15","#ffffff"];
  const [bg, primary, accent, text] = palette;
  const pages = bp.pages || [];

  const safe = (s) => String(s || "").replace(/[&<>]/g, c => ({"&":"&amp;","<":"&lt;",">":"&gt;"}[c]));
  const sections = pages.flatMap(p => (p.sections||[]).map(s => `
    <div style="margin-bottom:18px;">
      <h3 style="color:${primary};font-family:Outfit,sans-serif;margin:0 0 4px;">${safe(s.heading)}</h3>
      <p style="margin:0;color:#475569;line-height:1.6;">${safe(s.body)}</p>
    </div>`)).slice(0, 6).join("");

  return `<!doctype html><html><head><meta charset="utf-8">
<link href="https://fonts.googleapis.com/css2?family=Outfit:wght@600;800&family=Inter:wght@400;600&display=swap" rel="stylesheet">
<style>
  body { margin:0; font-family:Inter,sans-serif; color:#0f172a; background:#fafafa; }
  .hero { background:linear-gradient(160deg, ${bg}, ${primary}); color:${text}; padding:48px 28px; text-align:center; }
  .tagline { display:inline-block;padding:5px 14px;border:1px solid rgba(255,255,255,.25);border-radius:999px;font-size:11px;letter-spacing:.18em;text-transform:uppercase;margin-bottom:12px; }
  .hero h1 { font-family:Outfit,sans-serif;font-size:36px;margin:0 0 8px; }
  .hero p { font-size:14px;opacity:.9;margin:0 auto 18px;max-width:520px; }
  .cta { display:inline-block;padding:10px 22px;background:${accent};color:#0f172a;font-weight:700;border-radius:999px;text-decoration:none;font-family:Outfit,sans-serif; }
  .body { padding:30px 26px;max-width:760px;margin:0 auto; }
  .wm {
    position:fixed; right:12px; bottom:12px;
    background:#0f172a; color:#fff; padding:6px 12px; border-radius:999px;
    font-family:Outfit,sans-serif; font-size:11px; font-weight:600;
    box-shadow:0 4px 14px rgba(0,0,0,.3); display:inline-flex; align-items:center; gap:6px;
  }
  .wm .dot { width:6px;height:6px;border-radius:50%;background:#facc15;display:inline-block; }
  .wm .brand { color:#3b82f6; }
</style></head><body>
<section class="hero">
  ${tagline ? `<div class="tagline">${safe(tagline)}</div>` : ""}
  <h1>${safe(headline)}</h1>
  <p>${safe(sub)}</p>
  <a class="cta" href="#">${safe(cta)}</a>
</section>
<section class="body">${sections}</section>
<div class="wm"><span class="dot"></span>Built by <span class="brand">Optivize</span></div>
</body></html>`;
}
